import { Button, Table, Spinner, Card, Collapse } from "react-bootstrap";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { BuyTohon, checkBusinessHours } from "../../utils/Util";
import TokuteishohyokiMain from "../TokuteishohyokiMain";
import { useAuthStatus } from "../../context/AuthContext";
import { useTohonBuy } from "../../context/TohonBuyContext";
import { MAPPLE_COM_LINKS, TERMS_OF_USE_PATH } from "../../constants/Constant";

const BuyRegistryModal = ({ show, handleClose }) => {
  const { userInfo, isUserLoggedIn } = useAuthStatus();
  const { tohonBuyList } = useTohonBuy();

  const [isBuyable, setIsBuyable] = useState(false);
  const [buttonText, setButtonText] = useState("読み込み中...");
  const [isLoading, setIsLoading] = useState(true); // 営業時間チェック中
  const [isRedirecting, setIsRedirecting] = useState(false); // オンライン決済画面へ遷移中
  const [activeKey, setActiveKey] = useState(null);

  const handleToggle = (key) => {
    setActiveKey(activeKey === key ? null : key);
  };

  useEffect(() => {
    if (show) {
      setIsRedirecting(false);
      setIsLoading(true);
      checkBusinessHours().then(({ isBuyable, message }) => {
        setIsBuyable(isBuyable);
        setButtonText(message);
        setIsLoading(false);
      });
    }
  }, [show]);

  // Stripe Checkout へ全画面遷移した後にブラウザの「戻る」で返ってくると、
  // bfcache（戻る/進むキャッシュ）が JS 状態ごと復元するため isRedirecting=true のまま
  // スピナーが回り続ける。bfcache 復元（pageshow.persisted）と、非 bfcache の再表示
  // （visibilitychange）の両方でリセットする。
  useEffect(() => {
    const reset = () => setIsRedirecting(false);
    const onPageShow = (e) => {
      if (e.persisted) reset();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") reset();
    };
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const totalAmount = tohonBuyList.length * 700;
  const tax = Math.round(totalAmount * 0.1);
  const finalAmount = totalAmount + tax;

  const handleBuyClick = () => {
    if (!userInfo) return;
    const { sub: userid, email } = userInfo.attributes;
    // オンライン決済画面（Stripe Checkout）へ同一タブ遷移する。
    // 成功時はページを離れるため、購入リストのクリアは戻り先（PaymentSuccessOverlay）で行う。
    setIsRedirecting(true);
    BuyTohon(userid, email, tohonBuyList, isUserLoggedIn).catch((error) => {
      console.error("購入処理に失敗しました", error);
      alert("購入処理に失敗しました。");
      setIsRedirecting(false);
    });
  };

  if (!show) return null;

  // #bodyArea（position: fixed; z-index: 0）の内部だと、その内側の z-index はルート階層の
  // #headerArea（z-index: 50）に勝てないため、document.body へポータル出力して全面を覆う。
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="不動産登記情報（土地、全部事項）の購入画面"
      style={{
        position: "fixed",
        inset: 0,
        // ヘッダーのアカウントボタン・検索ボックスや広告（z-index: 9999）より前面に表示する
        zIndex: 10000,
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* ヘッダー（地図画面と同じマップルロゴ＋タイトル） */}
      <div
        style={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "0.25rem 0.5rem",
          borderBottom: "1px solid #dee2e6",
          backgroundColor: "#fff",
        }}
      >
        <img
          src="https://labs.mapple.com/mapple-logo.png"
          alt="mapple"
          className="mapplelogo"
        />
        <span style={{ fontWeight: "bold", fontSize: "1rem" }}>
          不動産登記情報（土地、全部事項）の購入画面
        </span>
      </div>

      {/* 本文（スクロール領域） */}
      <div style={{ flex: "1 1 auto", overflowY: "auto", padding: "1rem" }}>
        {/* 購入リストの表は横長画面で間延びしないよう最大1200pxで中央寄せ */}
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <Table>
          <thead>
            <tr>
              <th>#</th>
              <th>商品</th>
              <th>内容</th>
              <th>数量</th>
              <th>金額</th>
            </tr>
          </thead>
          <tbody>
            {tohonBuyList.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td className="align-middle">不動産登記情報（土地）</td>
                <td>
                  <span>{item.city}</span>
                  <br />
                  <span>
                    {item.oaza}
                    {item.chome}
                    {item.koaza}
                    {item.yobi}
                    {item.chiban}
                  </span>
                </td>
                <td className="align-middle">1</td>
                <td className="align-middle">700円</td>
              </tr>
            ))}
            <tr>
              <th scope="row"></th>
              <td colSpan="3">消費税（10％）</td>
              <td>{tax}円</td>
            </tr>
            <tr>
              <th scope="row"></th>
              <td colSpan="3">合計</td>
              <td>{finalAmount}円</td>
            </tr>
            <tr>
              <th scope="row"></th>
              <td colSpan="3">送付先</td>
              <td>{userInfo?.attributes.email}</td>
            </tr>
          </tbody>
        </Table>
        </div>
        <div className="row justify-content-center">
          <div className="d-flex flex-column align-items-center mb-3">
            <div className="mb-3">
              <Button
                variant="success"
                id="btn_BuyTohon"
                disabled={!isBuyable || isLoading || isRedirecting}
                onClick={handleBuyClick}
              >
                {(isLoading || isRedirecting) && (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-1"
                  />
                )}
                {isRedirecting
                  ? "オンライン決済画面へ移動しています..."
                  : buttonText}
              </Button>
            </div>
            <div className="row justify-content-center">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isRedirecting}
              >
                キャンセル
              </Button>
            </div>
          </div>

          <div
            className="p-2 d-flex justify-content-center"
            style={{ wordBreak: "normal", fontSize: "medium" }}
          >
            <div className="d-inline-block" style={{ textAlign: "left" }}>
              <span style={{ color: "red", fontWeight: "bold" }}>
                【決済サービス変更のお知らせ】
              </span>
              <br />
              2026年8月1日より、「不動産登記情報（土地）の購入機能」の決済サービスを
              <span style={{ textDecoration: "underline" }}>
                SquareからStripe（ストライプ）へ変更
              </span>
              いたします。
              <br />
              決済画面や領収書メールの送付元が変わりますが、価格や購入操作の変更はございません。
              <br />
              また、現在Stripeで決済されている「サブスクリプション」にも影響はございません。
              <br />
              何卒ご理解を賜りますようお願い申し上げます。
            </div>
          </div>

          {/* prettier-ignore */}
          <div
            className="p-2"
            style={{ wordBreak: "normal", fontSize: "small" }}
          >
            【備考】
            <br />１．不動産登記情報（土地）の取得は、<a href="https://www.torus.co.jp/" target="_blank"
              rel="noopener noreferrer">株式会社トーラス</a>のAPIを利用しています。
            <br />２．不動産登記情報（土地）の送信までに、<u>オンライン決済完了から<span style={{ color: "red" }}>15分程度</span>かかります</u>。
            <br />３．不動産登記情報（土地）は、
            MAPPLE法務局地図ビューアで登録されたお客様のメールアドレスあてに、メールの添付ファイル（PDF）として送付いたします。マップル（mapple_registry@mapple.co.jp）からのメールが受信できるよう、事前にご確認をお願いいたします。
            <br />４．メールが届かない場合、大変お手数ですが、<a href={MAPPLE_COM_LINKS.CONTACT_SITE} target="_blank" rel="noopener noreferrer">お問い合わせフォーム</a>に下記を記載の上、お問い合わせください。
            <br />　　・アカウント登録しているメールアドレス
            <br />　　・ご購入された地番（●●市△△町１００番地 等）
            <br />　　・ご購入のおおよその日時
            <br />５．本機能で購入できる不動産登記情報（土地）は、一般財団法人民亊法務協会「登記情報提供サービス」で購入できるものと同じです。
            <br />６．商品の特性上、返品、お客様都合のキャンセルは承っておりません。
            <br />７．検索結果で表示された場所はおおよその位置を表示していますので、お探しの住所（地番）の位置と異なることがあります。
            <br />８．地図上に表示している地番の情報が古い場合があります。
            <br />９．オンライン決済画面の有効期限は1時間です。無効になった場合は、再度、地番の選択から操作してください。
            <br />１０．オンライン決済画面の「カード情報を保存する」では、次回の決済からSMS（ショートメッセージ）を用いた認証コードでクレジットカード情報を自動入力できます。保存する場合は、オンライン決済画面のCONTACT欄にSMSを受信できる電話番号を入力してください。
            <br />１１．オンライン決済画面のメールアドレス欄はレシート受取用のアドレスです。不動産登記情報は、MAPPLE法務局地図ビューアでご登録いただいている上記の送付先アドレスにお送りいたしますので、ご注意ください。
          </div>

          {/* 規約類（アコーディオン） */}
          <div
            className="m-3"
            style={{ fontSize: "small" }}
          >
            <div className="row justify-content-start w-100">
              <div id="accordionBuyTohon" className="w-100">
                <Button
                  variant="link"
                  className="p-2 mb-3 btn-sm"
                  onClick={() => handleToggle("0")}
                  aria-controls="collapse-0"
                  aria-expanded={activeKey === "0"}
                >
                  アカウント利用規約
                </Button>
                <Button
                  variant="link"
                  className="p-2 mb-3 btn-sm"
                  onClick={() => handleToggle("1")}
                  aria-controls="collapse-1"
                  aria-expanded={activeKey === "1"}
                >
                  配送・送料について
                </Button>
                <Button
                  variant="link"
                  className="p-2 mb-3 btn-sm"
                  onClick={() => handleToggle("2")}
                  aria-controls="collapse-2"
                  aria-expanded={activeKey === "2"}
                >
                  お支払い方法について
                </Button>
                <Button
                  variant="link"
                  className="p-2 mb-3 btn-sm"
                  onClick={() => handleToggle("3")}
                  aria-controls="collapse-3"
                  aria-expanded={activeKey === "3"}
                >
                  特定商取引法に基づく表記
                </Button>

                <Collapse in={activeKey === "0"}>
                  <div id="collapse-0">
                    <Card>
                      <Card.Body style={{ padding: 0 }}>
                        <iframe
                          src={TERMS_OF_USE_PATH}
                          className="w-100 border-0"
                          height="300"
                          title="account-terms"
                        ></iframe>
                      </Card.Body>
                    </Card>
                  </div>
                </Collapse>
                <Collapse in={activeKey === "1"}>
                  <div id="collapse-1">
                    <Card className="border-0">
                      <Card.Body>
                        <p>【配送・送料について】</p>
                        メールの添付ファイル（PDFファイル）として送付します。メールの受信に必要な費用はお客様のご負担になります。
                      </Card.Body>
                    </Card>
                  </div>
                </Collapse>
                <Collapse in={activeKey === "2"}>
                  <div id="collapse-2">
                    <Card className="border-0">
                      <Card.Body>
                        <p>【支払い方法について】</p>
                        <p>クレジットカードのみのお取り扱いになります。</p>
                        <img
                          src="mapple01/mojmap/creditcardimage.png"
                          className="img-fluid"
                          style={{ maxWidth: "352px" }}
                          alt="creditcards"
                        />
                      </Card.Body>
                    </Card>
                  </div>
                </Collapse>
                <Collapse in={activeKey === "3"}>
                  <div id="collapse-3">
                    <Card className="border-0">
                      <Card.Body>
                        <TokuteishohyokiMain />
                      </Card.Body>
                    </Card>
                  </div>
                </Collapse>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BuyRegistryModal;
